import { chunk } from "@common/helpers/collections";
import { TRANSACTIONS_PAGE_SIZE } from "./useTransactionsQuery";

export interface TransactionsByIdsQueryResult<T extends { id: number }> {
    data?: { data: readonly T[] };
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: Error | null;
}

export function chunkTransactionIdsForQuery(
    transactionIds: readonly number[],
): number[][] {
    return chunk(transactionIds, TRANSACTIONS_PAGE_SIZE);
}

export function mergeTransactionsByRequestedIds<T extends { id: number }>({
    pages,
    transactionIds,
}: {
    pages: ReadonlyArray<{ data: readonly T[] } | undefined>;
    transactionIds: readonly number[];
}): T[] {
    const transactionsById = new Map<number, T>();
    for (const page of pages) {
        for (const transaction of page?.data ?? []) {
            transactionsById.set(transaction.id, transaction);
        }
    }
    return transactionIds.flatMap((transactionId) => {
        const transaction = transactionsById.get(transactionId);
        return transaction === undefined ? [] : [transaction];
    });
}

export function combineTransactionsByIdsQueryResults<T extends { id: number }>({
    results,
    transactionIds,
}: {
    results: ReadonlyArray<TransactionsByIdsQueryResult<T>>;
    transactionIds: readonly number[];
}): {
    data: T[];
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: Error | null;
} {
    if (results.length === 0) {
        return {
            data: [],
            isLoading: false,
            isError: false,
            isSuccess: true,
            error: null,
        };
    }
    const failedResult = results.find((result) => result.isError);
    return {
        data: mergeTransactionsByRequestedIds({
            pages: results.map((result) => result.data),
            transactionIds,
        }),
        isLoading: results.some((result) => result.isLoading),
        isError: failedResult !== undefined,
        isSuccess: results.every((result) => result.isSuccess),
        error: failedResult?.error ?? null,
    };
}
